import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { readProduct, searchProducts, updateProduct } from '../../api/business/endpoints';
import type { PackItemInput, ProductDetail, ProductSummary } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatNumber } from '../../components/format';
import {
  movedPackItem,
  packInputOf,
  parsePackQuantity,
  withoutPackItem,
  withPackItem,
  withPackQuantity,
} from '../../components/packItems';
import { useDebouncedValue } from '../../components/useDebouncedValue';
import { variantLabel } from '../../components/variants';
import { useLanguage } from '../../i18n/LanguageContext';

type ProductPackSectionProps = {
  companyId: string;
  product: ProductDetail;
  canManage: boolean;
};

/**
 * A pack's contents (R0030 WP-09): its items in order — each variant with its quantity per pack, what is on hand of
 * it and the packs it alone would allow, the scarcest marked — and the packs they make together. To whoever may manage
 * products: a picker over the company's standard products (a variable one asks for its variant), quantities edited in
 * place, arrows to reorder and a button to remove. Every change saves the whole list.
 */
export const ProductPackSection = ({ companyId, product, canManage }: ProductPackSectionProps) => {
  const { language, t } = useLanguage();
  const [term, setTerm] = useState('');
  const [picked, setPicked] = useState<ProductSummary | null>(null);
  const [variantId, setVariantId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [problem, setProblem] = useState<string | null>(null);
  const [editing, setEditing] = useState<Record<string, string>>({});
  const debounced = useDebouncedValue(term.trim(), 300);
  const items: Array<PackItemInput> = packInputOf(product.packItems);
  const candidates = useQuery({
    queryKey: ['pack-candidates', companyId, debounced],
    queryFn: () =>
      searchProducts(companyId, {
        term: debounced,
        status: 'ACTIVE',
        type: 'STANDARD',
        page: 1,
        sort: 'name',
        direction: 'asc',
      }),
    enabled: canManage,
  });
  const pickedDetail = useQuery({
    queryKey: ['product', companyId, picked?.id],
    queryFn: () => readProduct(companyId, picked?.id ?? ''),
    // A simple product's one variant is hidden in the list: its detail names it.
    enabled: picked !== null,
  });
  const save = useAppMutation(
    (packItems: Array<PackItemInput>) => updateProduct(companyId, product.id, { packItems }),
    ['product', 'products'],
    () => {
      setPicked(null);
      setVariantId('');
      setQuantity('1');
      setEditing({});
    },
  );
  const limiting = product.packItems.find((item) => item.limiting) ?? null;
  const choices = (pickedDetail.data?.variants ?? []).filter((variant) => variant.status === 'ACTIVE');

  const add = (): void => {
    const amount = parsePackQuantity(quantity);

    if (picked === null) return;

    if (amount === undefined) {
      setProblem(t('pack.quantityInvalid'));

      return;
    }

    // A simple product's one variant is the product's own; a variable one names the variant chosen.
    const chosen = picked.variantCount > 0 ? variantId : (pickedDetail.data?.variants[0]?.id ?? '');

    if (chosen === '') {
      setProblem(t('pack.variantRequired'));

      return;
    }

    setProblem(null);
    save.mutate(withPackItem(items, chosen, amount));
  };

  const commitQuantity = (itemVariantId: string): void => {
    const text = editing[itemVariantId];

    if (text === undefined) return;

    const amount = parsePackQuantity(text);

    if (amount === undefined) {
      setProblem(t('pack.quantityInvalid'));

      return;
    }

    setProblem(null);
    save.mutate(withPackQuantity(items, itemVariantId, amount));
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        spacing={1}
      >
        <Typography variant="h6">{t('pack.title')}</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            color="primary"
            variant="outlined"
            label={t('pack.available', { count: formatNumber(product.availableQuantity ?? 0, language) })}
          />
          {limiting ? (
            <Typography variant="body2" color="text.secondary">
              {t('pack.limitedBy', { name: limiting.name })}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
        {t('pack.hint')}
      </Typography>
      <ErrorNotice error={save.error} />
      {problem ? (
        <Alert severity="warning" variant="outlined" sx={{ mb: 1 }} onClose={() => setProblem(null)}>
          {problem}
        </Alert>
      ) : null}
      {product.packItems.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          {t('pack.empty')}
        </Typography>
      ) : (
        <List dense disablePadding>
          {product.packItems.map((item, index) => (
            <ListItem
              key={item.variantId}
              disableGutters
              sx={{ pr: canManage ? 26 : 0 }}
              secondaryAction={
                canManage ? (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <TextField
                      size="small"
                      label={t('pack.quantity')}
                      value={editing[item.variantId] ?? String(item.quantity)}
                      inputProps={{ inputMode: 'numeric', 'aria-label': t('pack.quantity') }}
                      sx={{ width: 96 }}
                      disabled={save.isPending}
                      onChange={(e) => setEditing({ ...editing, [item.variantId]: e.target.value })}
                      onBlur={() => commitQuantity(item.variantId)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitQuantity(item.variantId);
                      }}
                    />
                    <Tooltip title={t('pack.moveUp')}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={t('pack.moveUp')}
                          disabled={index === 0 || save.isPending}
                          onClick={() => save.mutate(movedPackItem(items, index, -1))}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('pack.moveDown')}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={t('pack.moveDown')}
                          disabled={index === product.packItems.length - 1 || save.isPending}
                          onClick={() => save.mutate(movedPackItem(items, index, 1))}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('pack.remove')}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={t('pack.remove')}
                          disabled={save.isPending}
                          onClick={() => save.mutate(withoutPackItem(items, item.variantId))}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                ) : undefined
              }
            >
              <ListItemAvatar>
                <Avatar variant="rounded" src={item.mainImage?.urls.thumb.webp} alt={item.name}>
                  {item.name.slice(0, 1)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <span>
                      {item.name}
                      {item.variantLabel ? ` — ${item.variantLabel}` : ''}
                    </span>
                    {canManage ? null : (
                      <Chip
                        size="small"
                        label={t('pack.perPack', { count: formatNumber(item.quantity, language) })}
                      />
                    )}
                    {item.limiting ? <Chip size="small" color="warning" label={t('pack.limiting')} /> : null}
                    {item.status === 'ARCHIVED' || item.variantStatus === 'ARCHIVED' ? (
                      <Chip size="small" color="warning" variant="outlined" label={t('pack.archived')} />
                    ) : null}
                  </Stack>
                }
                secondary={[
                  item.sku,
                  t('pack.onHand', { count: formatNumber(item.onHand, language) }),
                  t('pack.covers', { count: formatNumber(item.packsCovered, language) }),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
            </ListItem>
          ))}
        </List>
      )}
      {canManage ? (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ md: 'center' }}
          sx={{ mt: 1.5 }}
        >
          <Autocomplete<ProductSummary>
            sx={{ flex: 2, minWidth: 220 }}
            size="small"
            options={(candidates.data?.items ?? []).filter((option) => option.id !== product.id)}
            value={picked}
            loading={candidates.isFetching}
            disabled={save.isPending}
            filterOptions={(all) => all}
            getOptionLabel={(option) => [option.name, option.sku, option.ean].filter(Boolean).join(' · ')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Avatar
                  variant="rounded"
                  src={option.mainImage?.urls.thumb.webp}
                  alt={option.name}
                  sx={{ width: 28, height: 28, mr: 1.5 }}
                >
                  {option.name.slice(0, 1)}
                </Avatar>
                {[option.name, option.sku, option.ean].filter(Boolean).join(' · ')}
              </li>
            )}
            onChange={(_event, value) => {
              setPicked(value);
              setVariantId('');
            }}
            inputValue={term}
            onInputChange={(_event, value, reason) => {
              if (reason === 'input' || reason === 'clear') setTerm(value);
            }}
            renderInput={(params) => (
              <TextField {...params} label={t('pack.add')} placeholder={t('pack.searchHint')} />
            )}
          />
          {picked !== null && picked.variantCount > 0 ? (
            <TextField
              select
              size="small"
              label={t('pack.variant')}
              value={variantId}
              sx={{ flex: 1, minWidth: 160 }}
              onChange={(e) => setVariantId(e.target.value)}
            >
              {choices.map((variant) => (
                <MenuItem key={variant.id} value={variant.id}>
                  {variantLabel(variant, t)}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <TextField
            size="small"
            label={t('pack.quantity')}
            value={quantity}
            inputProps={{ inputMode: 'numeric' }}
            sx={{ width: 120 }}
            onChange={(e) => setQuantity(e.target.value)}
          />
          <Box>
            <Button
              variant="outlined"
              disabled={picked === null || pickedDetail.data === undefined || save.isPending}
              onClick={add}
            >
              {t('pack.addButton')}
            </Button>
          </Box>
        </Stack>
      ) : null}
    </Paper>
  );
};
